import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, StatusBadge } from "@/components/StatusBadge";
import SubmitDealDialog from "@/components/SubmitDealDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShoppingCart, HandCoins, ArrowRight, PackageOpen, Store, Tag, Pencil, PiggyBank } from "lucide-react";
import PayoutProfileCard from "@/components/PayoutProfileCard";

const PAYOUT_LABELS = {
  auto_queued: { text: "Payout auto-scheduled", cls: "bg-blue-100 text-blue-800" },
  pending: { text: "Payout pending review", cls: "bg-amber-100 text-amber-800" },
  transferred: { text: "Paid to wallet", cls: "bg-green-100 text-green-800" },
  failed: { text: "Payout needs attention", cls: "bg-red-100 text-red-700" },
};

export default function MyDeals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState(null);
  const [subs, setSubs] = useState(null);
  const [savings, setSavings] = useState(null);

  const loadSubs = () => api.get("/deals/submissions/mine").then(({ data }) => setSubs(data));

  useEffect(() => {
    api.get("/matches/mine").then(({ data }) => setMatches(data));
    api.get("/me/savings").then(({ data }) => setSavings(data)).catch(() => {});
    loadSubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>My deals</h1>
        <p className="mt-2 text-muted-foreground">Your active splits and the deals you've submitted.</p>

        {savings && savings.completed_count > 0 && (
          <motion.div
            data-testid="savings-badge"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="mt-6 flex items-center gap-4 rounded-2xl border border-accent/20 bg-accent/5 p-5"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 text-accent shrink-0">
              <PiggyBank className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">You've saved</p>
              <p data-testid="savings-total" className="font-heading text-3xl text-accent" style={{ fontWeight: 900 }}>
                {money(savings.total_saved)}
              </p>
            </div>
            <p className="ml-auto text-right text-sm text-muted-foreground">
              across <b className="text-foreground">{savings.completed_count}</b> completed split{savings.completed_count === 1 ? "" : "s"} 🎉
            </p>
          </motion.div>
        )}

        <Tabs defaultValue="splits" className="mt-8">
          <TabsList>
            <TabsTrigger value="splits" data-testid="tab-my-splits">My splits</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-my-submissions">
              My submissions{subs ? ` (${subs.length})` : ""}
            </TabsTrigger>
          </TabsList>

          {/* Splits */}
          <TabsContent value="splits" className="mt-6">
            <div className="mb-6">
              <PayoutProfileCard />
            </div>
            {matches === null ? (
              <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={`match-skel-${i}`} className="h-24 rounded-xl" />)}</div>
            ) : matches.length === 0 ? (
              <div className="mt-10 flex flex-col items-center text-center text-muted-foreground">
                <PackageOpen className="h-10 w-10 mb-3" />
                <p>No splits yet.</p>
                <Button className="mt-4 rounded-full" data-testid="browse-empty-btn" onClick={() => navigate("/browse")}>
                  Browse deals
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {matches.map((m, i) => {
                  const isBuyer = user.user_id === m.primary_user_id;
                  return (
                    <motion.div
                      key={m.match_id}
                      data-testid={`my-deal-${m.match_id}`}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="flex items-center gap-4 rounded-xl border border-black/10 bg-white p-4 hover:-translate-y-0.5 transition-transform duration-200"
                    >
                      <img src={m.deal?.image_url} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${isBuyer ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                            {isBuyer ? <ShoppingCart className="h-3 w-3" /> : <HandCoins className="h-3 w-3" />}
                            {isBuyer ? "Buyer" : "Partner"}
                          </span>
                          <StatusBadge status={m.status} />
                        </div>
                        <h3 className="mt-1 font-heading text-lg truncate" style={{ fontWeight: 600 }}>{m.deal?.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {m.agreed_fee != null ? `Fee: ${money(m.agreed_fee)}` : "Fee not agreed yet"}
                          {" · "}with {isBuyer ? (m.partner_name || "—") : m.primary_name}
                        </p>
                        {isBuyer && PAYOUT_LABELS[m.payout_status] && (
                          <span
                            data-testid={`payout-state-${m.match_id}`}
                            className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${PAYOUT_LABELS[m.payout_status].cls}`}
                          >
                            {PAYOUT_LABELS[m.payout_status].text}
                            {m.payout_status === "transferred" && m.payout_reference ? ` · ${m.payout_reference}` : ""}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm" data-testid={`open-room-${m.match_id}`}
                        onClick={() => navigate(`/room/${m.match_id}`)}
                        className="rounded-full shrink-0"
                      >
                        Open <ArrowRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Submissions */}
          <TabsContent value="submissions" className="mt-6">
            <div className="mb-4">
              <SubmitDealDialog onSubmitted={loadSubs} />
            </div>
            {subs === null ? (
              <div className="space-y-4">{[...Array(2)].map((_, i) => <Skeleton key={`sub-skel-${i}`} className="h-24 rounded-xl" />)}</div>
            ) : subs.length === 0 ? (
              <div className="mt-6 text-center text-muted-foreground">
                You haven't submitted any deals yet. Spotted a great BOGO? Share it above.
              </div>
            ) : (
              <div className="space-y-4">
                {subs.map((d, i) => (
                  <motion.div
                    key={d.deal_id}
                    data-testid={`my-submission-${d.deal_id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className="rounded-xl border border-black/10 bg-white p-4"
                  >
                    <div className="flex items-center gap-4">
                      {d.image_url ? (
                        <img src={d.image_url} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="h-16 w-16 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                          <Tag className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <StatusBadge status={d.status} />
                        <h3 className="mt-1 font-heading text-lg truncate" style={{ fontWeight: 600 }}>{d.title}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Store className="h-3.5 w-3.5" /> {d.retailer} · {money(d.original_price)}
                        </p>
                      </div>
                      <SubmitDealDialog
                        editDeal={d}
                        onSubmitted={loadSubs}
                        trigger={
                          <Button size="sm" variant="outline" data-testid={`edit-submission-${d.deal_id}`} className="rounded-full border-black/20 shrink-0">
                            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                          </Button>
                        }
                      />
                    </div>
                    {d.status === "rejected" && d.reject_reason && (
                      <p className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2 text-sm text-red-700">
                        Reason: {d.reject_reason}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
